/**
 * Утилиты краша: множитель, часы, форматирование.
 *
 * Ключевое правило (§2 краш-документации): множитель хранится и сравнивается
 * В СОТЫХ (целое число, 214 = 2.14×). Делить на 100 ТОЛЬКО при выводе на экран.
 *
 * Единственное исключение — autoCashout в crash:place-bet передаётся в иксах (2.0),
 * а возвращается во всех событиях в сотых (200).
 */

/**
 * Сотые → строка вида «2.14» для вывода на экран.
 * @param {number} hundreds — значение в сотых (214 → "2.14")
 */
export const hundredsToX = (hundreds) => {
  const value = Math.floor(Number(hundreds || 0))
  // Округление вниз ближе к серверной арифметике (§6)
  return (value / 100).toFixed(2)
}

/**
 * Сотые → строка вида «2.14×» с символом ×.
 */
export const hundredsToXWithSymbol = (hundreds) => {
  return `${hundredsToX(hundreds)}×`
}

/**
 * Иксы (2.0) → сотые (200). Используется для отправки autoCashout в place-bet.
 * Сервер сам переводит 2.0 → 200, но мы присылаем в иксах по спеке.
 */
export const xToHundreds = (x) => {
  const num = Number(x)
  if (!Number.isFinite(num) || num <= 0) return 0
  return Math.floor(num * 100)
}

/**
 * Рассчитать множитель в сотых по формуле роста.
 * Формула из §6: multiplier = Math.exp(growth * elapsedSec)
 * Считаем в сотых, округляя вниз (ближе к серверной арифметике).
 *
 * @param {number} startTime — unix-мс старта раунда (из crash:run.startTime)
 * @param {number} growth — темп роста (из crash:run.growth)
 * @param {number} clockOffset — поправка часов: serverTime - Date.now() (из crash:time)
 * @returns {number} множитель в сотых (100 = 1.00×)
 */
export const calcMultiplierHundreds = (startTime, growth, clockOffset = 0) => {
  const now = Date.now() + clockOffset
  const elapsedSec = Math.max(0, (now - startTime) / 1000)
  const multiplier = Math.exp(growth * elapsedSec)
  // округление вниз, в сотых
  return Math.floor(multiplier * 100)
}

/**
 * Сумма ставки в кристаллах из объекта crash:bet / crash:players.
 * amount = crystal + itemsSum (§4 краш-документации).
 */
export const getBetTotalAmount = (bet) => {
  return Number(bet?.amount || 0)
}

/**
 * Расчёт текущего выигрыша по ставке: amount × multiplier / 100.
 * @param {number} amount — общая сумма ставки (кристаллы + скины в кристаллах)
 * @param {number} multiplierHundreds — множитель в сотых
 * @returns {number} выигрыш в кристаллах
 */
export const calcWin = (amount, multiplierHundreds) => {
  const amountNum = Number(amount || 0)
  const mult = Number(multiplierHundreds || 0)
  return Math.floor((amountNum * mult) / 100)
}

/**
 * Признак «своя ставка» в записи игрока — сравнение по chatId.
 */
export const isMyBet = (player, myChatId) => {
  if (!player || !myChatId) return false
  return String(player.chatId) === String(myChatId)
}

/**
 * Цвет точки краха для ленты истории.
 * Ниже CRASH_LOW_THRESHOLD (2.00×) → красный, иначе зелёный.
 */
export const getCrashPointColor = (crashPointHundreds, lowThreshold = 200) => {
  return Number(crashPointHundreds || 0) < lowThreshold ? 'red' : 'green'
}

/**
 * Форматирование времени «N сек/мин/ч назад» для экрана истории.
 * finished_sec_ago приходит с бэкенда уже посчитанным.
 */
export const formatTimeAgo = (seconds, t) => {
  const sec = Number(seconds || 0)
  if (sec < 60) return t('crash.history.timeAgoSec', { count: sec })
  if (sec < 3600) return t('crash.history.timeAgoMin', { count: Math.floor(sec / 60) })
  return t('crash.history.timeAgoHour', { count: Math.floor(sec / 3600) })
}
